import * as React from "react";
import { toast } from "sonner";
import { RestrictedWordDialog } from "@/features/attorney/components/v2/RestrictedWordDialog";
import { useRestrictedWords, useSettingsCache } from "@/shared/cache";
import type { Entry } from "@/shared/types/entries";
import {
	detectRestrictedWords,
	mergeOverlappingMatches,
} from "@/shared/utils/restrictedWords";
import { patchDescription } from "@/v2/hooks/actions/patchDescription";
import type { ActionDeps } from "@/v2/hooks/actions/types";

type Flagged = {
	entry: Entry;
	/** Current text — the entry's own narrative until the user edits it here. */
	draft: string;
	hasBlocking: boolean;
};

/**
 * Pre-export restricted-word gate for bulk actions.
 *
 * The entry form checks restricted words against an open editor; a bulk export
 * has no editor, so nothing stopped flagged narratives from exporting. This
 * sweeps the selection first, then steps the user through only the flagged
 * entries in the shared dialog.
 *
 * Warnings inform but never block: a skipped entry still exports. Entries that
 * still carry block-severity words are held back from the export and reported.
 */
export function useExportRestrictedWordGate(deps: ActionDeps) {
	const { settings } = useSettingsCache();
	const { getRestrictedWords } = useRestrictedWords();

	const [queue, setQueue] = React.useState<Flagged[]>([]);
	const [index, setIndex] = React.useState(0);
	const [isOpen, setIsOpen] = React.useState(false);
	const proceedRef = React.useRef<((entries: Entry[]) => void) | null>(null);
	const timekeeperRef = React.useRef<string | null | undefined>(undefined);
	const cleanRef = React.useRef<Entry[]>([]);
	/** Whole selection size — the labels speak for the export, not the flagged subset. */
	const [totalCount, setTotalCount] = React.useState(0);
	/**
	 * True from the moment the dialog closes until the export has been handed off.
	 * The dialog stops blocking input before the description saves finish, and the
	 * callers run this gate ahead of their own mutation, so nothing else would stop
	 * a second export from landing mid-flight and being wiped by the first reset().
	 */
	const finalizingRef = React.useRef(false);

	/** Words for one entry, scoped by its own client/matter. */
	const wordsFor = React.useCallback(
		(entry: Entry) => {
			const matterId = entry.matter?.id ?? undefined;
			const clientCode = matterId?.split(".")[0];
			const matterCode = matterId?.split(".").slice(1).join(".");
			return getRestrictedWords(clientCode, matterCode);
		},
		[getRestrictedWords],
	);

	const reset = React.useCallback(() => {
		setIsOpen(false);
		setQueue([]);
		setIndex(0);
		proceedRef.current = null;
		cleanRef.current = [];
		timekeeperRef.current = undefined;
		setTotalCount(0);
	}, []);

	/**
	 * Run `proceed` with the entries that may export. Opens the dialog first if
	 * any selected narrative is flagged.
	 */
	const guardExport = React.useCallback(
		(
			entries: Entry[],
			proceed: (allowed: Entry[]) => void,
			timekeeperId?: string | null,
		) => {
			if (finalizingRef.current) {
				toast.info("Finishing the previous export — try again in a moment.");
				return;
			}

			if (!settings.check_for_red_words || entries.length === 0) {
				proceed(entries);
				return;
			}

			const flagged: Flagged[] = [];
			const clean: Entry[] = [];

			for (const entry of entries) {
				const words = wordsFor(entry);
				const matches =
					words.length && entry.description
						? mergeOverlappingMatches(
								detectRestrictedWords(entry.description, words),
							)
						: [];

				if (matches.length === 0) {
					clean.push(entry);
					continue;
				}
				flagged.push({
					entry,
					draft: entry.description,
					hasBlocking: matches.some((m) => m.severity === "block"),
				});
			}

			if (flagged.length === 0) {
				proceed(entries);
				return;
			}

			proceedRef.current = proceed;
			cleanRef.current = clean;
			timekeeperRef.current = timekeeperId;
			setQueue(flagged);
			setTotalCount(entries.length);
			setIndex(0);
			setIsOpen(true);
		},
		[settings.check_for_red_words, wordsFor],
	);

	/** Keep the current entry's edit, then move within the flagged set. */
	const handleStep = React.useCallback(
		(direction: -1 | 1, current: string) => {
			setQueue((prev) =>
				prev.map((f, i) => (i === index ? { ...f, draft: current } : f)),
			);
			setIndex((i) => Math.min(Math.max(i + direction, 0), queue.length - 1));
		},
		[index, queue.length],
	);

	/** Primary action: persist every correction, then export what's allowed. */
	const handleExport = React.useCallback(
		async (current: string) => {
			const finalQueue = queue.map((f, i) =>
				i === index ? { ...f, draft: current } : f,
			);
			const proceed = proceedRef.current;
			const clean = cleanRef.current;
			const timekeeperId = timekeeperRef.current;
			finalizingRef.current = true;
			setIsOpen(false);
			try {
				const edited = finalQueue.filter(
					(f) => f.draft !== f.entry.description && f.draft.trim().length > 0,
				);

				const saved = new Set<string>();
				const unsaved = new Set<string>();
				for (const f of edited) {
					try {
						await patchDescription(deps, f.entry.id, f.draft, timekeeperId);
						saved.add(f.entry.id);
					} catch {
						// Keep going: one failed save shouldn't strand the rest of the batch.
						unsaved.add(f.entry.id);
					}
				}

				// Re-evaluate against the text that actually persisted — a correction may
				// have cleared the entry's blocking words.
				const allowed: Entry[] = [...clean];
				let blocked = 0;
				for (const f of finalQueue) {
					// The narrative the attorney asked for never reached the server.
					// Exporting the stale text would ship the wording they just rejected,
					// so hold the entry back and let them retry.
					if (unsaved.has(f.entry.id)) continue;

					const persisted = saved.has(f.entry.id);
					const text = persisted ? f.draft : f.entry.description;
					const words = wordsFor(f.entry);
					const matches = words.length
						? mergeOverlappingMatches(detectRestrictedWords(text, words))
						: [];
					if (matches.some((m) => m.severity === "block")) {
						blocked += 1;
						continue;
					}
					allowed.push(persisted ? { ...f.entry, description: text } : f.entry);
				}

				if (unsaved.size > 0) {
					toast.error(
						unsaved.size === 1
							? "Couldn't save your fix for 1 entry — it wasn't exported. Try again."
							: `Couldn't save your fixes for ${unsaved.size} entries — they weren't exported. Try again.`,
					);
				}
				if (blocked > 0) {
					toast.warning(
						blocked === 1
							? "1 entry was held back — it still contains blocked words."
							: `${blocked} entries were held back — they still contain blocked words.`,
					);
				}

				reset();
				if (allowed.length > 0) proceed?.(allowed);
			} finally {
				// Always released: a stuck flag would refuse every later export.
				finalizingRef.current = false;
			}
		},
		[queue, index, deps, wordsFor, reset],
	);

	const currentEntry = queue[index];

	const dialog = currentEntry ? (
		<RestrictedWordDialog
			// Remount per entry: the dialog syncs its local text on `narrative`
			// changing, so two entries sharing identical text would otherwise keep
			// the first one's edit in the textarea — and save it under the wrong id.
			key={currentEntry.entry.id}
			open={isOpen}
			narrative={currentEntry.draft}
			date={new Date(currentEntry.entry.date)}
			matterId={currentEntry.entry.matter?.id ?? undefined}
			restrictedWords={wordsFor(currentEntry.entry)}
			stepIndex={index + 1}
			stepCount={queue.length}
			onStep={handleStep}
			primaryLabel={
				totalCount > 1 ? `Export all ${totalCount} entries` : "Export"
			}
			onBack={reset}
			onSave={handleExport}
		/>
	) : null;

	return { guardExport, restrictedWordGate: dialog };
}
